<template>
  <DataTable
    :value="conversations"
    dataKey="id"
    filterDisplay="row"
    v-model:filters="filters"
    v-model:expandedRows="expandedRows"
    sortField="created"
    :sortOrder="-1"
    scrollable
    scrollHeight="100%"
    style="height: 100%"
  >
    <Column expander style="width: 5rem" />
    <Column
      field="created"
      header="Date"
      sortable
      --filterField="created"
      dataType="date"
    >
      <template #body="{ data }">
        {{ formatDate(data.created) }}
      </template>
    </Column>
    <Column field="user.displayName" header="User">
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="users"
          placeholder="Any"
          style="min-width: 14rem"
        >
        </MultiSelect>
      </template>
    </Column>
    <Column field="title" header="Title"></Column>
    <Column field="broken" header="Broken" dataType="boolean">
      <template #body="{ data }">
        <i
          class="pi"
          :class="{
            'pi-check-circle text-green-500': !data.broken,
            'pi-times-circle text-red-400': data.broken,
          }"
        ></i>
      </template>

      <template #filter="{ filterModel, filterCallback }">
        <Checkbox
          v-model="filterModel.value"
          :indeterminate="filterModel.value === null"
          binary
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column field="feedback.sentiment" header="Feedback">
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="feedbacks"
          placeholder="Any"
          style="min-width: 14rem"
          :maxSelectedLabels="2"
        >
        </MultiSelect>
      </template>
    </Column>
    <Column field="messagesAmount" header="Messages" sortable></Column>
    <Column field="allMessagesAmount" header="All Messages" sortable></Column>
    <template #expansion="slotProps">
      <div v-if="slotProps.data.feedback.text">
        {{ slotProps.data.feedback.text }}
      </div>
      <div v-if="slotProps.data.error">
        {{ slotProps.data.error }}
      </div>
      <div v-if="slotProps.data.logs">{{ slotProps.data.logs }}</div>
      <Button rounded label="Show" @click="openInspector(slotProps.data)" />
      <Dialog v-model:visible="dialogOpen" header="Details" modal>
        <Inspector></Inspector>
      </Dialog>
    </template>
  </DataTable>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import type { AdminConversation } from "@/src/api/chat";
import { FilterMatchMode, FilterOperator } from "@primevue/core/api";
import Inspector from "./../graph/ChatBot/Inspector.vue";
// import Column from "primevue/column";

const store = useStore();

const expandedRows = ref({});
const dialogOpen = ref(false);

const filters = ref({
  "user.displayName": { value: null, matchMode: FilterMatchMode.IN },
  "feedback.sentiment": { value: null, matchMode: FilterMatchMode.IN },
  broken: { value: null, matchMode: FilterMatchMode.EQUALS },
  created: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_AFTER }],
  },
});

const conversations = computed<AdminConversation[]>(() => {
  const loaded: AdminConversation[] =
    store.getters["chatbotStore/conversations"];
  loaded.forEach(c => {
    const names = [
      [c.user.first_name, c.user.last_name].filter(n => n).join(" "),
      c.user.email,
      c.user.username,
    ];
    c.user.displayName = names.filter(n => n)[0] as string;
    const date: string | Date = c.created as any;
    if (!(date instanceof Date)) {
      c.created = new Date(date);
    }
  });
  return loaded;
});

const users = computed(() => [
  ...new Set(conversations.value.map(c => c.user.displayName)),
]);
const feedbacks = computed(() => [
  ...new Set(conversations.value.map(c => c.feedback.sentiment)),
]);

const formatDate = value => {
  return value.toLocaleDateString("en-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    time: "24-hour",
  });
};

onMounted(() => {
  console.log("ADMIN MOUNTED");
  console.log(store);
  store.dispatch("chatbotStore/adminLoadAllConversations");
});

const openInspector = (data: AdminConversation) => {
  store.commit("repoStore/setGraphId", data.meta.graph_id);
  store.commit("chatbotStore/setConversation", { conversationId: data.id });
  dialogOpen.value = true;
};
</script>

<style scoped></style>
